import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Grid, GridCell, ProductionStatusSelector } from 'v1/components/shared';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { Capability } from '__types__';

import { displayAlert, openModal } from 'store/v1/ui/ui.actions.js';
import { getProductionResourceIds } from 'v1/helpers/productionHelper';
import ReactTooltip from 'react-tooltip';

import './ProductionSidebarDrawerHeader.scss';
import { useShortlistBatchAdd } from '~/v4/entities/common/hooks/useShortlistBatchAdd';
import {
  RestrictedDropdownItem,
  RestrictedDropdownV5
} from 'v1/components/shared/buttons/RestrictedDropdown/RestrictedDropdown.jsx';
import { Expand, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Button } from 'v5/design-system/Button.js';
import { DropdownMenuItem } from 'v5/design-system/DropdownMenu.js';

const ProductionSidebarDrawerHeader = ({ production, renderCoreField }) => {
  const [codeCopyState, setCodeCopyState] = useState();
  const { id, icon, code, title } = production;

  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const batchAddToShortlist = useShortlistBatchAdd();

  useEffect(() => {
    ReactTooltip.rebuild();
    if (codeCopyState) {
      dispatch(displayAlert('success', 'Production code copied'));
      setTimeout(() => {
        setCodeCopyState(false);
      }, 500);
    }
  }, [codeCopyState]);

  return (
    <div className="SidebarDrawerHeader">
      <Grid vcenter gutters="S">
        {icon && (
          <GridCell width="auto" className="ProductionSidebarDrawerHeader-icon">
            <div className="ProductionSidebarDrawerHeader-icon">{icon}</div>
          </GridCell>
        )}
        <GridCell className="BookingSidebarDrawerHeader-main">
          <div className="ProductionSidebarDrawerHeader-title">
            <CopyToClipboard
              text={code || id}
              onCopy={() => setCodeCopyState(true)}
            >
              <span
                data-tip="Click to copy production id"
                className="ProductionSidebarDrawerHeader-id"
              >
                {code || id}
              </span>
            </CopyToClipboard>
            {code && !title ? (
              <h2>{code}</h2>
            ) : (
              renderCoreField('title', {
                appearance: 'silent',
                size: 'S',
                label: null
              })
            )}
          </div>
        </GridCell>
        <GridCell width="auto">
          <ProductionStatusSelector production={production} />
        </GridCell>
        <GridCell width="auto" className="tailwind-root">
          <RestrictedDropdownV5
            capabilitiesAtLeastOne={[
              Capability.PRODUCTION_READ,
              Capability.PRODUCTION_UPDATE
            ]}
            trigger={
              <Button variant="ghost" size="md" layout="icon">
                <MoreHorizontal />
              </Button>
            }
            align="end"
          >
            <RestrictedDropdownItem capabilities={Capability.PRODUCTION_READ}>
              <DropdownMenuItem asChild>
                <Link to={`/app/${auth.app_id}/productions/${id}`}>
                  <Expand />
                  Go to full view
                </Link>
              </DropdownMenuItem>
            </RestrictedDropdownItem>
            <RestrictedDropdownItem capabilities={Capability.SHORTLIST_UPDATE}>
              <DropdownMenuItem
                onSelect={() => {
                  batchAddToShortlist(getProductionResourceIds(production));
                }}
              >
                <img src="/images/icon_sidebar_shortlist.svg" alt="" />
                <span className="MenuItem-label">Batch add to shortlist</span>
              </DropdownMenuItem>
            </RestrictedDropdownItem>
            <RestrictedDropdownItem capabilities={Capability.PRODUCTION_UPDATE}>
              <DropdownMenuItem
                onSelect={() => {
                  dispatch(
                    openModal('ProductionSetUpModal', {
                      production,
                      jumpTo: 'details'
                    })
                  );
                }}
              >
                <Pencil />
                Edit Production
              </DropdownMenuItem>
            </RestrictedDropdownItem>
            <RestrictedDropdownItem
              capabilities={Capability.PRODUCTION_ARCHIVE}
            >
              <DropdownMenuItem
                onSelect={() => {
                  const options = {
                    title: `Are you sure you want to archive this production?`,
                    text: '',
                    button: 'Confirm Archive',
                    action: 'DELETE_PRODUCTION',
                    production_id: production.id,
                    stopRedirect: true
                  };
                  dispatch(openModal('ConfirmationModal', options));
                }}
              >
                <Trash2 className="text-fg-danger" />
                Archive Production
              </DropdownMenuItem>
            </RestrictedDropdownItem>
          </RestrictedDropdownV5>
        </GridCell>
      </Grid>
    </div>
  );
};

ProductionSidebarDrawerHeader.propTypes = {
  production: PropTypes.shape(), // TODO: type
  renderCoreField: PropTypes.func
};

export default ProductionSidebarDrawerHeader;
